import { useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Loader2, Upload, DownloadCloud, Download, Camera } from 'lucide-react'
import { useMdmCommands, useSendMdmCommand, useUploadMdmFile } from '../../../hooks/useMdm'
import { mdmApi } from '../../../services/mdmApi'
import { formatDateTimeSeconds } from '../../../utils/format'
import { CommandStatusBadge } from './mobileUi'
import { COMMAND_LABELS, errorMessage, saveBlob } from './mobileUtils'

const FILE_COMMANDS = ['push-file', 'pull-file', 'screenshot']

/** Envio/coleta de arquivos no armazenamento compartilhado do tablet (/sdcard). */
export default function FilesTab({ deviceId, readOnly }) {
  const upload = useUploadMdmFile(deviceId)
  const cmd = useSendMdmCommand(deviceId)
  const { data: commands = [] } = useMdmCommands(deviceId, 100)
  const fileRef = useRef(null)

  const [pushFile, setPushFile] = useState(null)
  const [pushPath, setPushPath] = useState('')
  const [pullPath, setPullPath] = useState('Download/')
  const [progress, setProgress] = useState(null)
  const [msg, setMsg] = useState(null)

  const fileCommands = useMemo(() => commands.filter((c) => FILE_COMMANDS.includes(c.type)), [commands])

  const pickFile = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setPushFile(f)
    setPushPath(`Download/${f.name}`)
  }

  const handlePush = async () => {
    if (!pushFile || !pushPath.trim()) return
    setMsg(null)
    setProgress(0)
    try {
      const uploaded = await upload.mutateAsync({ file: pushFile, onProgress: setProgress })
      await cmd.mutateAsync({ type: 'push-file', payload: { fileId: uploaded.id ?? uploaded.fileId, path: pushPath.trim() } })
      setMsg({ ok: true, text: `"${pushFile.name}" enviado — o tablet vai gravar em ${pushPath.trim()}.` })
      setPushFile(null)
      setPushPath('')
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao enviar o arquivo.') })
    } finally {
      setProgress(null)
    }
  }

  const handlePull = async () => {
    const path = pullPath.trim()
    if (!path) return
    setMsg(null)
    try {
      await cmd.mutateAsync({ type: 'pull-file', payload: { path } })
      setMsg({ ok: true, text: 'Pedido enviado. Quando o tablet responder, o arquivo aparece na lista abaixo.' })
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e) })
    }
  }

  const handleScreenshot = async () => {
    setMsg(null)
    try {
      await cmd.mutateAsync({ type: 'screenshot' })
      setMsg({ ok: true, text: 'Captura solicitada.' })
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e) })
    }
  }

  const download = async (c) => {
    setMsg(null)
    try {
      const blob = await mdmApi.downloadFile(deviceId, c.result.fileId)
      const name = c.type === 'screenshot'
        ? `screenshot-${c.id}.jpg`
        : String(c.payload?.path || '').split('/').pop() || `arquivo-${c.id}`
      saveBlob(blob, name)
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao baixar.') })
    }
  }

  const busy = upload.isPending || cmd.isPending

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="grid md:grid-cols-2 gap-4">
          <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Enviar arquivo para o tablet</h3>
            <input ref={fileRef} type="file" className="hidden" onChange={pickFile} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {pushFile ? pushFile.name : 'Escolher arquivo'}
            </button>
            <label className="block text-xs text-gray-600">Destino (relativo a /sdcard)</label>
            <input type="text" value={pushPath} onChange={(e) => setPushPath(e.target.value)} placeholder="Download/arquivo.pdf"
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono" />
            <button type="button" onClick={handlePush} disabled={busy || !pushFile || !pushPath.trim()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50">
              {upload.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {progress != null ? `Enviando ${progress}%` : 'Enviar'}
            </button>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Buscar arquivo do tablet</h3>
            <label className="block text-xs text-gray-600">Caminho (relativo a /sdcard)</label>
            <input type="text" value={pullPath} onChange={(e) => setPullPath(e.target.value)} placeholder="Download/relatorio.pdf"
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono" />
            <div className="flex gap-2">
              <button type="button" onClick={handlePull} disabled={busy || !pullPath.trim()}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50">
                <DownloadCloud className="w-4 h-4" /> Buscar
              </button>
              <button type="button" onClick={handleScreenshot} disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <Camera className="w-4 h-4" /> Capturar tela
              </button>
            </div>
          </section>
        </div>
      )}

      {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Operação</th>
              <th className="text-left px-3 py-2">Caminho</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Quando</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {fileCommands.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">Nenhuma transferência ainda.</td></tr>
            ) : fileCommands.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">{COMMAND_LABELS[c.type] || c.type}</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{c.result?.path || c.payload?.path || '—'}</td>
                <td className="px-3 py-2">
                  <CommandStatusBadge status={c.status} />
                  {c.error && <div className="text-xs text-red-600 mt-0.5">{c.error}</div>}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateTimeSeconds(c.completedAt || c.createdAt)}</td>
                <td className="px-3 py-2 text-right">
                  {c.result?.fileId && (
                    <button type="button" onClick={() => download(c)} className="inline-flex items-center gap-1 text-xs text-theme-primary hover:underline">
                      <Download className="w-3.5 h-3.5" /> Baixar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
